import { createRoute, redirect } from '@tanstack/react-router';
import type { Permission } from '@voll-intelligence/voll-authz';

import { queryClient, queryKeys } from '@web/core/query';
import { meRepository } from '@web/data/repositories';
import { ApiKeysPage } from '@web/ui/admin/api_keys';
import { IntegrationsPage } from '@web/ui/admin/integrations';
import { ProfilePage, SettingsLayout, TriageConfigPage } from '@web/ui/settings/views';
import { MembersPage } from '@web/ui/members/views';

import { orgRoute } from './org_routes';

/**
 * Guard an org settings sub-route on one permission. Reads `/me` and
 * `/me/permissions` from the cache primed by `orgRoute.loader` (which also
 * already validated org membership and `accounts:access`), then bounces to `/`
 * when the caller lacks `permission`.
 */
async function ensureOrgPermission(orgSlug: string, permission: Permission): Promise<void> {
  const me = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });
  const org = me.orgs.find((o) => o.slug === orgSlug);
  if (!org) return redirect({ throw: true, to: '/' }) as never;

  const perms = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.permissions(org.id),
    queryFn: () => meRepository.getMyPermissions(org.id),
  });
  if (!perms.permissions.includes(permission)) {
    return redirect({ throw: true, to: '/' }) as never;
  }
}

export const ensureCanReadMembers = (orgSlug: string) =>
  ensureOrgPermission(orgSlug, 'accounts:members:read');

export const settingsLayoutRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'settings',
  component: SettingsLayout,
});

export const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    redirect({
      throw: true,
      to: '/orgs/$orgSlug/settings/profile',
      params: { orgSlug: params.orgSlug },
    });
  },
});

export const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'profile',
  component: ProfilePage,
});

export const settingsMembersRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'members',
  component: MembersPage,
  beforeLoad: ({ params }) => ensureCanReadMembers(params.orgSlug),
});

/**
 * Staff-only console for agent API keys. Sits inside the settings shell
 * (so the sidebar nav can link to it next to Members), but the guard is
 * platform-level (`isStaff`) — org owners are NOT privileged here.
 */
async function ensureIsStaff(): Promise<void> {
  let me;
  try {
    me = await queryClient.ensureQueryData({
      queryKey: queryKeys.me.all,
      queryFn: () => meRepository.getMe(),
    });
  } catch {
    return redirect({ throw: true, to: '/' }) as never;
  }
  if (!me.isStaff) return redirect({ throw: true, to: '/' }) as never;
}

export const settingsApiKeysRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'api-keys',
  component: ApiKeysPage,
  beforeLoad: () => ensureIsStaff(),
});

/** Integrações: by `kanban:integrations:read` — a chat tool is linked per corporate group. */
export const settingsIntegrationsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'integrations',
  component: IntegrationsPage,
  beforeLoad: ({ params }) => ensureCanReadIntegrations(params.orgSlug),
});

export const ensureCanReadTriageConfig = (orgSlug: string) =>
  ensureOrgPermission(orgSlug, 'kanban:triage_config:read');

export const ensureCanReadIntegrations = (orgSlug: string) =>
  ensureOrgPermission(orgSlug, 'kanban:integrations:read');

export const settingsTriageRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'triage',
  component: TriageConfigPage,
  beforeLoad: ({ params }) => ensureCanReadTriageConfig(params.orgSlug),
});
